// How a time zone is chosen in the admin website (company, client location, district, district manager,
// guard): it is worked out, not picked. The server has the same rule and the last word (App\TimeZones).
// - A country's zones come from the server (timezones/country), so both sides use one list: its current
//   IANA zones, never stand-ins without summer time such as "GMT" or "UTC".
// - A client location: the zone at its map position (@photostructure/tz-lookup, which works offline from
//   the time zone boundary maps), when that zone is one of its country's.
// - Otherwise the country's only zone; or, when the country has several, the admin picks one of them.
import tzlookup from '@photostructure/tz-lookup'
import { auth } from '../Api'

const countryZones = {}

// The country's zones ("GB" -> ["Europe/London"]), from the server; [] for none. Remembered per country.
export function loadCountryZones(country) {
	const code = country ? String(country).trim().toUpperCase() : ''
	if (code === '') {
		return Promise.resolve([])
	}
	if (!countryZones[code]) {
		countryZones[code] = auth.getCountryTimezones({ country: code })
			.then((res) => (res && res.data && Array.isArray(res.data.zones) ? res.data.zones : []))
			.catch(() => {
				delete countryZones[code]
				return []
			})
	}
	return countryZones[code]
}

// The zone at a map position ("Europe/London"), or null when there is no usable position.
export function zoneFromPosition(latitude, longitude) {
	const lat = parseFloat(latitude)
	const lng = parseFloat(longitude)
	if (!isFinite(lat) || !isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) {
		return null
	}
	try {
		return tzlookup(lat, lng) || null
	} catch (e) {
		return null
	}
}

// The zone for a record: { zone, source, positionZone }.
// source: 'position' (a location's map position), 'country' (the country's only zone), 'choice' (the
// country has several: zone is the admin's choice, or null until one is picked), or 'none' (no zones).
// positionZone is the zone at the position even when it is not one of the country's (a hint that the
// country may be wrong).
export function decideZone(zones, current, latitude, longitude) {
	const positionZone = latitude !== undefined ? zoneFromPosition(latitude, longitude) : null
	if (!zones || zones.length === 0) {
		return { zone: null, source: 'none', positionZone }
	}
	if (positionZone && zones.indexOf(positionZone) !== -1) {
		return { zone: positionZone, source: 'position', positionZone }
	}
	if (zones.length === 1) {
		return { zone: zones[0], source: 'country', positionZone }
	}
	return { zone: zones.indexOf(current) !== -1 ? current : null, source: 'choice', positionZone }
}
